import Vue from 'vue';
import { Component, Watch} from 'vue-property-decorator';
import { getSignInPath, Routes } from '@/router';
import { Milestone } from '@/models/leaderboard';
import { SummerExtrasLeaderboardPageLoadEvent } from '@/services/analytics/events';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';


@Component({
  name: 'CmgLeaderboard',
})
export default class CmgLeaderboard extends Vue {
  @Watch('shouldRedirect')
  handleShouldRedirectChange(newValue: boolean): void {
    if (newValue) {
      window.location.replace(`${window.location.origin}/summer-dashboard`);
    }
  }

  @Watch('showCompletedMilestoneModal', { immediate: true })
  async handleShowCompletedMilestoneModalChange(newValue: boolean): Promise<void> {
    if (newValue && !this.termsAndConditionsModalVisible) {
      await this.showAllEligibleMilestoneModals();
    }
  }
  
  get milestoneCompletedModalVisible(): boolean {
    return this.$appStore.state.ui.modals.summerLeaderboardMilestoneCompleted.visible;
  }

  get cyoRewardModalVisible(): boolean {
    return this.$appStore.state.ui.modals.summerLeaderboardCyoReward.visible;
  }

  get termsAndConditionsModalVisible(): boolean {
    return this.$appStore.state.ui.modals.rewardTermsAndConditions.visible;
  }

  get showCompletedMilestoneModal(): boolean {
    if (!this.isCyoRewardAndSideQuestsEnabled) { return false; }
    const milestones = this.eligibleForChooseYourOwnRewardMilestones();
    return milestones.length > 0;
  }

  get isShowingModals(): boolean {
    const summerLeaderboardMilestoneCompletedVisibile = this.$appStore.state.ui.modals.summerLeaderboardMilestoneCompleted.visible;
    const summerLeaderboardCyoRewardVisible = this.$appStore.state.ui.modals.summerLeaderboardCyoReward.visible;
    const summerLeaderboardCyoRedemptionSuccessModalVisible = this.$appStore.state.ui.modals.summerLeaderboardCyoRedemptionSuccessModal.visible;
    const rewardTermsAndConditionsVisible = this.$appStore.state.ui.modals.rewardTermsAndConditions.visible;
    return summerLeaderboardMilestoneCompletedVisibile || summerLeaderboardCyoRewardVisible || rewardTermsAndConditionsVisible || summerLeaderboardCyoRedemptionSuccessModalVisible;
  }

  async waitForModalsToClose(): Promise<void> {
    while (this.isShowingModals) {
      const summerLeaderboardMilestoneCompletedVisible = this.$appStore.state.ui.modals.summerLeaderboardMilestoneCompleted.visible;
      const summerLeaderboardCyoRewardVisible = this.$appStore.state.ui.modals.summerLeaderboardCyoReward.visible;
      const rewardTermsAndConditionsVisible = this.$appStore.state.ui.modals.rewardTermsAndConditions.visible;
      const summerLeaderboardCyoRedemptionSuccessModalVisible = this.$appStore.state.ui.modals.summerLeaderboardCyoRedemptionSuccessModal.visible;

      if (summerLeaderboardMilestoneCompletedVisible) {
        await this.$services.ui.waitForModalToClose('summerLeaderboardMilestoneCompleted');
      } else if (summerLeaderboardCyoRewardVisible) {
        await this.$services.ui.waitForModalToClose('summerLeaderboardCyoReward');
      } else if (rewardTermsAndConditionsVisible) {
        await this.$services.ui.waitForModalToClose('rewardTermsAndConditions');
      } else if (summerLeaderboardCyoRedemptionSuccessModalVisible) {
        await this.$services.ui.waitForModalToClose('summerLeaderboardCyoRedemptionSuccessModal');
      }
    }
  }

  eligibleForChooseYourOwnRewardMilestones(): Milestone[] {
    // Get all milestones that are eligible for choose your own reward and haven't been shown yet.
    // Leaderboard service orders by summer campaign month and milestone segment.
    const allMilestones = this.$appStore.state.leaderboard.summerCampaigns.flatMap(c => c.milestones);
    return allMilestones
      .filter(m => m.eligibleForChooseYourOwnReward)
      .filter(m => localStorage.getItem(`summerLeaderboardMilestoneCompletedShown_${m.month}_${m.segment}`) !== 'true');
  }

  async showAllEligibleMilestoneModals(): Promise<void> {
    const milestones = this.eligibleForChooseYourOwnRewardMilestones();
    
    for (const milestone of milestones) {
      if (this.$router.currentRoute.name !== Routes.Leaderboard) {
        break;
      }

      this.$services.ui.setModalVisibility('summerLeaderboardMilestoneCompleted', true, { milestone });
      await this.waitForModalsToClose();
      localStorage.setItem(`summerLeaderboardMilestoneCompletedShown_${milestone.month}_${milestone.segment}`, 'true');
    }
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get isInErrorState(): boolean {
    return this.$appStore.state.leaderboard.isInErrorState;
  }

  get isInMissingCampaignState(): boolean {
    return this.$appStore.state.leaderboard.summerCampaigns.length === 0;
  }

  async mounted() {
    try {
      this.setLoadingStatus(true);

      await this.$services.featureFlags.waitForSplitReady();

      const isLoggedIn = await this.$services.customer.waitForCustomerInitialization();
      if (!isLoggedIn) {
        this.$router.push(getSignInPath(this.$route.path));
        return;
      }

      const isEnrolled = this.$appStore.state.customer.loyalty.isEnrolled;
      const isOptedIn = this.isOptedInSummerOfChipotle;
      if (!isEnrolled) {
        this.$router.replace({ name: Routes.EditAccount });
        return;
      }
      if (!isOptedIn) {
        this.$router.replace({ name: Routes.RewardsExtras });
        return;
      }

      await this.$services.leaderboard.getLeaderboardData();
      this.$services.analytics.send(new SummerExtrasLeaderboardPageLoadEvent());

    } catch (err) {
      this.$services.logger.error('Could not load Leaderboard');
    } finally {
      this.setLoadingStatus(false);
    }

  }

  setLoadingStatus(loading: boolean) {
    this.$services.ui.setAppBlur(loading);
    this.$services.ui.setAppPepperVisibility(loading);
  }

  get shouldRedirect(): boolean {
    return this.isSplitReady && !this.isSummerOfExtrasEnabled;
  }

  get isSplitReady(): boolean {
    return !!this.$appStore.state.featureFlags.customerFlagsKey;
  }

  get isSummerOfExtrasEnabled(): boolean {
    return this.$appStore.state.featureFlags.featureFlags.isSummerOfExtrasEnabled;
  }

  get isCyoRewardAndSideQuestsEnabled(): boolean {
    return this.$appStore.state.featureFlags.featureFlags.isCyoRewardAndSideQuestsEnabled;
  }

  get isOptedInSummerOfChipotle(): boolean {
    return this.$appStore.getters[CUSTOMER_GETTERS.IS_OPTED_IN_SUMMER_OF_CHIPOTLE];
  }

  get leaderboardMapBackgroundTexture(): string {
    return this.$appStore.state.cms.images.loyalty.leaderboardMapBackgroundTexture.default;
  }
}
